import { useMemo, useState } from "react";
import { Alert, Button, Input, Progress, Table } from "reactstrap";
import { useNavigate } from "react-router-dom";
import PageContainer from "@components/layout/PageContainer";
import LoadingScreen from "@components/content/LoadingScreen";
import { showAlert } from "@utils/Alert";
import { buildColumnStats } from "@utils/import/columnStats";
import { buildColumnNames, buildDataRows, detectHeaderRow } from "@utils/import/detectHeaderRow";
import { ParsedTabularFile, TabularFileError, parseTabularFile } from "@utils/import/parseTabularFile";
import {
    ColumnMapping,
    DuplicatePolicy,
    ImportDefinition,
    ImportProgress,
    ImportSummary,
    ResolveResult,
} from "@utils/import/core/types";
import styles from "./style.module.scss";

type Step = "file" | "mapping" | "preview" | "done";

/** "auto" deja decidir a la heuristica; "none" fuerza columnas Columna1..N. */
type HeaderMode = "auto" | "none" | number;

const PREVIEW_ROWS = 5;
const MAX_PREVIEW_ROWS = 50;

const POLICY_LABELS: Record<DuplicatePolicy, string> = {
    skip: "Omitir los duplicados",
    update: "Actualizar los existentes",
    create: "Importar de todos modos",
};

const STATUS_LABELS = {
    new: { text: "Nueva", color: "text-success" },
    duplicate: { text: "Duplicada", color: "text-secondary" },
    warning: { text: "Advertencia", color: "text-warning" },
    error: { text: "Error", color: "text-danger" },
} as const;

type ImportWizardProps<TData, TContext> = {
    definition: ImportDefinition<TData, TContext>;
};

/**
 * Asistente de importacion masiva desde CSV/XLSX, agnostico del modelo.
 *
 * Los cuatro pasos (archivo, mapeo, vista previa, resultado) son siempre los mismos: lo
 * que cambia entre clientes, productos y lo que venga despues vive en la `ImportDefinition`
 * que se recibe por props.
 */
const ImportWizard = <TData, TContext>({ definition }: ImportWizardProps<TData, TContext>) => {
    const navigate = useNavigate();

    const [step, setStep] = useState<Step>("file");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const [fileName, setFileName] = useState("");
    const [parsed, setParsed] = useState<ParsedTabularFile | null>(null);
    const [sheetIndex, setSheetIndex] = useState(0);
    const [headerMode, setHeaderMode] = useState<HeaderMode>("auto");

    const [mapping, setMapping] = useState<ColumnMapping>({});

    const [policy, setPolicy] = useState<DuplicatePolicy>("skip");
    const [context, setContext] = useState<TContext | null>(null);
    const [resolved, setResolved] = useState<ResolveResult<TData> | null>(null);

    const [isImporting, setIsImporting] = useState(false);
    const [progress, setProgress] = useState<ImportProgress>({ processed: 0, total: 0 });
    const [summary, setSummary] = useState<ImportSummary | null>(null);
    const [skippedCount, setSkippedCount] = useState(0);

    const rawRows = useMemo(() => parsed?.sheets[sheetIndex]?.rows || [], [parsed, sheetIndex]);
    const detection = useMemo(() => detectHeaderRow(rawRows), [rawRows]);

    const headerRowIndex = useMemo(() => {
        if (headerMode === "auto") return detection.headerRowIndex;
        if (headerMode === "none") return null;
        return headerMode;
    }, [headerMode, detection.headerRowIndex]);

    const columnNames = useMemo(
        () => buildColumnNames(rawRows, headerRowIndex, detection.columnCount),
        [rawRows, headerRowIndex, detection.columnCount]
    );

    const dataRows = useMemo(() => buildDataRows(rawRows, headerRowIndex), [rawRows, headerRowIndex]);

    const columnStats = useMemo(
        () => buildColumnStats(dataRows, detection.columnCount),
        [dataRows, detection.columnCount]
    );

    const missingRequired = useMemo(() => {
        const mapped = new Set(Object.values(mapping).filter(Boolean));
        return definition.requiredFields.filter((field) => !mapped.has(field));
    }, [mapping, definition]);

    const onFileSelected = async (file?: File) => {
        if (!file) return;

        setError("");
        setIsLoading(true);
        try {
            const result = await parseTabularFile(file);
            setParsed(result);
            setSheetIndex(result.defaultSheetIndex);
            setFileName(file.name);
            setHeaderMode("auto");
            setMapping({});
        } catch (parseError) {
            const message =
                parseError instanceof TabularFileError
                    ? parseError.message
                    : "No fue posible leer el archivo seleccionado.";
            setError(message);
            setParsed(null);
        } finally {
            setIsLoading(false);
        }
    };

    const goToMapping = () => {
        // El mapeo automatico es solo una sugerencia: se puede cambiar columna por columna.
        setMapping(definition.suggestMapping(columnNames, dataRows));
        setStep("mapping");
    };

    const changeMapping = (columnIndex: number, fieldName: string) => {
        setMapping((current) => {
            const next: ColumnMapping = { ...current, [columnIndex]: fieldName || null };

            // Un atributo solo puede recibir una columna.
            if (fieldName) {
                Object.keys(next).forEach((key) => {
                    if (Number(key) !== columnIndex && next[Number(key)] === fieldName) {
                        next[Number(key)] = null;
                    }
                });
            }

            return next;
        });
    };

    const goToPreview = async () => {
        setIsLoading(true);
        setError("");
        try {
            const loaded = await definition.loadContext();
            setContext(loaded);
            setResolved(definition.resolveRows({ dataRows, mapping, context: loaded, policy }));
            setStep("preview");
        } catch (loadError) {
            setError(
                `No fue posible cargar los ${definition.entityPlural} actuales: ${(loadError as Error).message}`
            );
        } finally {
            setIsLoading(false);
        }
    };

    const changePolicy = (nextPolicy: DuplicatePolicy) => {
        setPolicy(nextPolicy);
        if (!context) return;
        setResolved(definition.resolveRows({ dataRows, mapping, context, policy: nextPolicy }));
    };

    const runImport = async () => {
        if (!resolved || !context) return;

        // A proposito no se usa `isLoading`: el LoadingScreen es un bloqueo a pantalla
        // completa y taparia la barra de progreso.
        setIsImporting(true);
        setError("");
        try {
            const result = await definition.commit({
                resolved,
                context,
                mapping,
                onProgress: setProgress,
            });

            setSkippedCount(result.skippedCount);
            setSummary(result.summary);
            setStep("done");
            showAlert(
                "success",
                `Se importaron ${result.summary.createdCount + result.summary.updatedCount} ${definition.entityPlural}`
            );
        } catch (importError) {
            setError(`La importacion fallo: ${(importError as Error).message}`);
            showAlert("danger", "No fue posible completar la importacion");
        } finally {
            setIsImporting(false);
        }
    };

    const renderSteps = () => {
        const stepIndex = { file: 1, mapping: 2, preview: 3, done: 4 }[step];

        return (
            <div className={styles.steps}>
                {[
                    { number: 1, label: "Archivo" },
                    { number: 2, label: "Mapeo" },
                    { number: 3, label: "Vista previa" },
                    { number: 4, label: "Resultado" },
                ].map((item) => (
                    <div
                        key={item.number}
                        className={`${styles.stepDot} ${stepIndex >= item.number ? styles.stepDotActive : ""}`}
                    >
                        <span>{item.number}</span>
                        <small>{item.label}</small>
                    </div>
                ))}
            </div>
        );
    };

    const renderFileStep = () => (
        <div className={styles.section}>
            <p className={styles.help}>{definition.fileHelpText}</p>

            <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                data-testid="import-file"
                onChange={(event) => onFileSelected(event.target.files?.[0])}
            />

            {parsed && (
                <>
                    {parsed.sheets.length > 1 && (
                        <div className={styles.field}>
                            <label htmlFor="import-sheet">Hoja</label>
                            <Input
                                id="import-sheet"
                                type="select"
                                data-testid="import-sheet"
                                value={sheetIndex}
                                onChange={(event) => setSheetIndex(Number(event.target.value))}
                            >
                                {parsed.sheets.map((sheet, index) => (
                                    <option key={sheet.name} value={index}>
                                        {sheet.name}
                                    </option>
                                ))}
                            </Input>
                        </div>
                    )}

                    <div className={styles.field}>
                        <label htmlFor="import-header-mode">Fila de encabezados</label>
                        <Input
                            id="import-header-mode"
                            type="select"
                            data-testid="import-header-mode"
                            value={`${headerMode}`}
                            onChange={(event) => {
                                const value = event.target.value;
                                setHeaderMode(value === "auto" || value === "none" ? value : Number(value));
                            }}
                        >
                            <option value="auto">
                                Automatico
                                {detection.headerRowIndex === null
                                    ? " (el archivo no parece traer encabezados)"
                                    : ` (fila ${detection.headerRowIndex + 1})`}
                            </option>
                            {rawRows.slice(0, PREVIEW_ROWS).map((_, index) => (
                                <option key={index} value={index}>
                                    Fila {index + 1}
                                </option>
                            ))}
                            <option value="none">Sin encabezados</option>
                        </Input>
                    </div>

                    <p className={styles.help}>
                        {dataRows.length} filas de datos, {columnNames.length} columnas.
                    </p>

                    <div className={styles.tableWrapper}>
                        <Table bordered size="sm" responsive>
                            <thead>
                                <tr>
                                    {columnNames.map((name) => (
                                        <th key={name}>{name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {dataRows.slice(0, PREVIEW_ROWS).map((row, index) => (
                                    <tr key={index}>
                                        {columnNames.map((name, columnIndex) => (
                                            <td key={name}>{row[columnIndex]}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </>
            )}
        </div>
    );

    const renderMappingStep = () => (
        <div className={styles.section}>
            <p className={styles.help}>{definition.mappingHelpText}</p>

            {missingRequired.length > 0 && (
                <Alert color="warning" data-testid="import-missing-required">
                    Falta asignar: {missingRequired.map((field) => definition.findField(field)?.label).join(", ")}
                </Alert>
            )}

            <div className={styles.tableWrapper}>
                <Table bordered size="sm" responsive>
                    <thead>
                        <tr>
                            <th>Columna del archivo</th>
                            <th>Ejemplo</th>
                            <th>Con datos</th>
                            <th>Dato de destino</th>
                        </tr>
                    </thead>
                    <tbody>
                        {columnNames.map((name, columnIndex) => (
                            <tr key={`${name}-${columnIndex}`}>
                                <td>{name}</td>
                                <td className={styles.sample}>{columnStats[columnIndex]?.sample}</td>
                                <td
                                    className={columnStats[columnIndex]?.filled ? undefined : styles.empty}
                                    data-testid={`import-filled-${columnIndex}`}
                                >
                                    {columnStats[columnIndex]?.filled ?? 0} de {dataRows.length}
                                </td>
                                <td>
                                    <Input
                                        type="select"
                                        bsSize="sm"
                                        data-testid={`import-mapping-${columnIndex}`}
                                        value={mapping[columnIndex] || ""}
                                        onChange={(event) => changeMapping(columnIndex, event.target.value)}
                                    >
                                        <option value="">No importar</option>
                                        {definition.fields.map((field) => (
                                            <option key={field.name} value={field.name}>
                                                {field.label}
                                            </option>
                                        ))}
                                    </Input>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </div>
    );

    const renderPreviewStep = () => {
        if (!resolved) return null;

        const counts = resolved.rows.reduce<Record<string, number>>(
            (acc, row) => ({ ...acc, [row.action]: (acc[row.action] || 0) + 1 }),
            {}
        );
        const errorCount = resolved.rows.filter((row) => row.status === "error").length;
        // Las filas con error tambien tienen action "skip"; se descuentan para no
        // confundirlas con los duplicados omitidos a proposito.
        const skippedDuplicates = (counts.skip || 0) - errorCount;
        const missingLookups = definition.missingLookups(resolved);

        return (
            <div className={styles.section}>
                <div className={styles.field}>
                    <span>{definition.duplicateQuestion}</span>
                    {(Object.keys(POLICY_LABELS) as DuplicatePolicy[]).map((option) => (
                        <label key={option} className={styles.radio}>
                            <input
                                type="radio"
                                name="duplicate-policy"
                                data-testid={`import-policy-${option}`}
                                checked={policy === option}
                                onChange={() => changePolicy(option)}
                            />{" "}
                            {POLICY_LABELS[option]}
                        </label>
                    ))}
                </div>

                <p className={styles.help} data-testid="import-preview-summary">
                    Se crearan {counts.create || 0} {definition.entityPlural}, se actualizaran{" "}
                    {counts.update || 0} y se omitiran {skippedDuplicates} por estar duplicados.
                </p>

                {errorCount > 0 && (
                    <Alert color="warning" data-testid="import-error-rows">
                        {definition.errorRowsHelp(errorCount, resolved.rows.length)}
                    </Alert>
                )}

                {missingLookups && (
                    <Alert color="info" data-testid="import-new-lookups">
                        {missingLookups.label}: {missingLookups.names.join(", ")}
                    </Alert>
                )}

                <div className={styles.tableWrapper}>
                    <Table bordered size="sm" responsive>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Estado</th>
                                {definition.previewColumns.map((column) => (
                                    <th key={column.header}>{column.header}</th>
                                ))}
                                <th>Observaciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {resolved.rows.slice(0, MAX_PREVIEW_ROWS).map((row) => (
                                <tr key={row.rowIndex}>
                                    <td>{row.rowIndex + 1}</td>
                                    <td className={STATUS_LABELS[row.status].color}>
                                        {STATUS_LABELS[row.status].text}
                                    </td>
                                    {definition.previewColumns.map((column) => (
                                        <td key={column.header}>{column.render(row)}</td>
                                    ))}
                                    <td className={styles.sample}>{row.messages.join(". ")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                {resolved.rows.length > MAX_PREVIEW_ROWS && (
                    <p className={styles.help}>
                        Mostrando las primeras {MAX_PREVIEW_ROWS} filas de {resolved.rows.length}.
                    </p>
                )}

                {progress.total > 0 && (
                    <Progress value={progress.processed} max={progress.total} data-testid="import-progress">
                        {progress.processed} / {progress.total}
                    </Progress>
                )}
            </div>
        );
    };

    const renderDoneStep = () => (
        <div className={styles.section} data-testid="import-result">
            <Alert color="success">
                Se crearon {summary?.createdCount || 0} {definition.entityPlural} y se actualizaron{" "}
                {summary?.updatedCount || 0}. Se omitieron {skippedCount}.
            </Alert>

            {!!summary?.failures.length && (
                <Alert color="danger">
                    {summary.failures.length} filas no se pudieron importar:
                    <ul>
                        {summary.failures.slice(0, 10).map((failure) => (
                            <li key={failure.index}>
                                Fila {failure.index + 1}: {failure.message}
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            <p className={styles.help}>{definition.doneHelpText}</p>

            <Button color="primary" onClick={() => navigate(definition.backUrl)}>
                {definition.doneActionTitle}
            </Button>
        </div>
    );

    const primaryActions = () => {
        if (step === "file") {
            return [
                {
                    actionTitle: "Continuar",
                    color: "success",
                    disabled: !dataRows.length,
                    onClick: goToMapping,
                },
            ];
        }
        if (step === "mapping") {
            return [
                { actionTitle: "Volver", color: "secondary", onClick: () => setStep("file") },
                {
                    actionTitle: "Continuar",
                    color: "success",
                    disabled: missingRequired.length > 0,
                    onClick: goToPreview,
                },
            ];
        }
        if (step === "preview") {
            return [
                {
                    actionTitle: "Volver",
                    color: "secondary",
                    disabled: isImporting,
                    onClick: () => setStep("mapping"),
                },
                {
                    actionTitle: isImporting ? "Importando..." : "Importar",
                    color: "success",
                    // Deshabilitado mientras corre para que un doble clic no importe dos veces.
                    disabled: isImporting || !resolved?.rows.some((row) => row.action !== "skip"),
                    onClick: runImport,
                },
            ];
        }
        return [];
    };

    return (
        <PageContainer title={definition.title} backUrl={definition.backUrl} primaryActions={primaryActions()}>
            {renderSteps()}

            {!!fileName && <p className={styles.fileName}>Archivo: {fileName}</p>}
            {!!error && (
                <Alert color="danger" data-testid="import-error">
                    {error}
                </Alert>
            )}

            {step === "file" && renderFileStep()}
            {step === "mapping" && renderMappingStep()}
            {step === "preview" && renderPreviewStep()}
            {step === "done" && renderDoneStep()}

            <LoadingScreen isLoading={isLoading} />
        </PageContainer>
    );
};

export default ImportWizard;
